import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StudentModel, StudentRequest } from '@generativ/much-smarter-models';
import { FormService } from '../../../core/services/form.service';
import { FeedbackService } from '../../../core/feedback/feedback.service';
import { StudentService } from '../student.service';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, SpinnerComponent],
    providers: [DatePipe]
})
export class ProfileComponent implements OnInit, OnDestroy {
  subscriptions: Subscription = new Subscription();
  profileForm: UntypedFormGroup;
  ready = true;
  changePassword = false;
  currentYear = new Date().getFullYear();
  minDate = '1900-01-01';
  maxDate = this.datePipe.transform(Date.now().toString(), 'yyyy-MM-dd');
  dateError: string;

  constructor(public formService: FormService,
              private route: ActivatedRoute,
              private router: Router,
              private authService: AuthService,
              private studentService: StudentService,
              private feedbackService: FeedbackService,
              private datePipe: DatePipe) {
    this.profileForm = this.formService.createForm(this.formOptions);
  }

  ngOnInit() {
    this.subscriptions.add(this.studentService.getProfile().subscribe(
      studentModel => {
        this.profileForm.patchValue({
          ...studentModel,
          birthdate: this.toDateInputValue(studentModel.birthdate),
        });
      }
    ));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  submitForm() {
    if (this.formService.validateFormAndUpdateErrors(this.profileForm)) {
      const student = new StudentRequest.Update();
      const formValue = this.profileForm.value;
      student.parse({
        ...formValue,
        birthdate: this.toTimestamp(formValue.birthdate),
      });
      this.ready = false;
      this.subscriptions.add(this.studentService.updateProfile(student).subscribe(
        (result) => {
          this.authService.setCurrentStudent(new StudentModel().parse(result));
          this.ready = true;
          this.router.navigate(['../']);
        },
        (err) => {
          this.ready = true;
          this.feedbackService.error(err, true);
        }
      ));
    }
  }

  private  get formOptions(): Record<string, unknown> {
    return {
      email: [
        null,
        Validators.compose([
          Validators.required,
          Validators.email
        ])
      ],
      firstName: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      lastName: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      birthdate: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      graduationYear: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      currentScoreEnglish: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(1),
          Validators.max(36)
        ])
      ],
      currentScoreMath: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(1),
          Validators.max(36)
        ])
      ],
      currentScoreReading: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(1),
          Validators.max(36)
        ])
      ],
      currentScoreScience: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(1),
          Validators.max(36)
        ])
      ],
      goalScoreEnglish: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(1),
          Validators.max(36)
        ])
      ],
      goalScoreMath: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(1),
          Validators.max(36)
        ])
      ],
      goalScoreReading: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(1),
          Validators.max(36)
        ])
      ],
      goalScoreScience: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(1),
          Validators.max(36)
        ])
      ],
    };
  }

  resetPassword(): void {
    this.subscriptions.add(
      this.authService.forgotPassword(this.authService.currentStudent.email)
        .subscribe(() => {
          console.log('reset');
          this.feedbackService.success(
            'A reset password email has been sent. Follow the email instructions to reset your password.');
        })
    );
  }

  onDateChange(event: any) {
    const date = Date.parse(event.target.value);
    if (date >= Date.parse(this.minDate) && date <= Date.parse(this.maxDate)) {
      this.dateError = null;
      this.profileForm.get('birthdate').setErrors(null);
    } else {
      this.dateError = 'Date must be between ' + this.minDate + ' and ' + this.maxDate;
      this.profileForm.get('birthdate').setErrors({invalid: true});
    }
  }

  private toDateInputValue(value: string | number | Date | null | undefined): string | null {
    if (value == null || value === '') {
      return null;
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
  }

  private toTimestamp(value: string | number | null | undefined): number | undefined {
    if (value == null || value === '') {
      return undefined;
    }

    if (typeof value === 'number') {
      return value;
    }

    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? undefined : timestamp;
  }

}
